import { Entity } from '../../entity';
import { EntityViewFilter } from '../../entities/views/entity.view.filter';
import { VisitApprovalCapturedWork, VisitApprovalChangeCounts, VisitApprovalEvidenceRef, VisitApprovalMembership, VisitApprovalReviewContext, VisitApprovalRevisionEntry, VisitApprovalScope, VisitApprovalValidityState } from './visit.approval';
import { VisitApprovalAction, VisitApprovalAttribution, VisitApprovalExistingTarget, VisitApprovalSelectionRow, VisitApprovalTarget, VisitApprovalTerminalOutcome } from './visit.approval.operation';
import { VisitComplianceWindow } from '../visit/visit.compliance.window';

export enum VisitApprovalCommandId {
    Current = 'VisitApprovalCurrentQuery',
    Get = 'VisitApprovalGetQuery',
    List = 'VisitApprovalListQuery',
    Approve = 'VisitApprovalApproveCommand',
    Prepare = 'VisitApprovalPrepareCommand',
    PrepareSelected = 'VisitApprovalPrepareSelectedCommand',
    Preparation = 'VisitApprovalPreparationQuery',
    PreparedEntries = 'VisitApprovalPreparedEntriesQuery',
    Result = 'VisitApprovalResultQuery',
    Withdraw = 'VisitApprovalWithdrawCommand',
    Reapprove = 'VisitApprovalReapproveCommand',
    Entries = 'VisitApprovalEntriesQuery',
    Revisions = 'VisitApprovalRevisionsQuery',
    RevisionChanges = 'VisitApprovalRevisionChangesQuery',
    History = 'VisitApprovalHistoryQuery',
    Review = 'VisitApprovalReviewQuery',
    Progress = 'VisitApprovalProgressQuery',
}

/**
 * What a Visit's approval standing reads as. The first three mirror the owning membership's
 * `VisitApprovalValidityState`; the last two only ever mean "no membership".
 */
export enum VisitApprovalStandingStatus {
    Approved = 'Approved',
    NeedsReview = 'NeedsReview',
    CheckPending = 'CheckPending',
    NotApproved = 'NotApproved',
    /** Its Service Line's `requiresApproval: false` policy stands in for approval. No approval record exists. */
    AutoApproved = 'AutoApproved',
}

/** Current authority and immutable attribution for one Visit, checked against live source epochs. */
export interface VisitApprovalStanding {
    approvalStatus: VisitApprovalStandingStatus;
    approval?: Entity;
    approvedBy?: Entity;
    approvedAt?: Date;
    approvalReason?: string;
    /** The winning membership's evidence pointer: a withdrawal must quote it, and no Visit read carries the membership. */
    approvalEvidence?: VisitApprovalEvidenceRef;
}
/** `serviceLineId` lets an occurrence no approval owns answer from its line's auto-approve policy. */
export interface VisitApprovalCurrentRequest { occurrenceId: string; scope: VisitApprovalScope; serviceLineId?: string; }
export interface VisitApprovalSelection { occurrenceId: string; reviewToken: string; }
export interface VisitApprovalWithdrawal { occurrenceId: string; expectedEvidence: VisitApprovalEvidenceRef; reason: string; }
export interface VisitApprovalRequestBase { requestId: string; scope: VisitApprovalScope; context: VisitApprovalReviewContext; }
export interface VisitApprovalApproveRequest extends VisitApprovalRequestBase { target: VisitApprovalTarget; visits: VisitApprovalSelection[]; }
export interface VisitApprovalWithdrawRequest extends VisitApprovalRequestBase { target: VisitApprovalExistingTarget; visits: VisitApprovalWithdrawal[]; }
export interface VisitApprovalReapproveRequest extends VisitApprovalRequestBase { target: VisitApprovalExistingTarget; visits: VisitApprovalSelection[]; }
export interface VisitApprovalPrepareRequest extends VisitApprovalRequestBase {
    action: VisitApprovalAction.Create | VisitApprovalAction.Add | VisitApprovalAction.Reapprove;
    target: VisitApprovalTarget;
    review: VisitApprovalReviewFilter;
}
export interface VisitApprovalPrepareSelectedRequest extends VisitApprovalPrepareRequest { selections: VisitApprovalSelection[]; }
export interface VisitApprovalOperationRequest { operationId: string; scope: VisitApprovalScope; }
export interface VisitApprovalResultRequest { requestId: string; scope: VisitApprovalScope; }
export interface VisitApprovalPendingResult { kind: 'Pending'; operationId: string; state: 'AwaitingAdmission' | 'Preparing' | 'Ready' | 'Processing'; }
export interface VisitApprovalRecordedResult { kind: 'Recorded'; operationId: string; outcome: VisitApprovalTerminalOutcome; }
export type VisitApprovalRequestResult = VisitApprovalPendingResult | VisitApprovalRecordedResult;
export interface VisitApprovalRowPageRequest extends VisitApprovalOperationRequest { afterOccurrenceId?: string; limit: number; }
export interface VisitApprovalSelectionPage { items: VisitApprovalSelectionRow[]; total: number; nextOccurrenceId?: string; sealVersion?: string; eventRevision: number; }
export interface VisitApprovalReviewFilter { from: string; to: string; timezone: string; search?: string; filters?: EntityViewFilter[]; }
export interface VisitApprovalPageRequest { scope: VisitApprovalScope; cursor?: string; limit: number; readToken?: string; }
export interface VisitApprovalScopedCounts { matchedTotal: number; fullTotal?: number; }
export interface VisitApprovalExactValidityCounts { kind: 'Exact'; approved: number; checkPending: number; needsReview: number; }
export interface VisitApprovalPendingValidityCounts { kind: 'Pending'; }
export type VisitApprovalEffectiveValidityCounts = VisitApprovalExactValidityCounts | VisitApprovalPendingValidityCounts;
export interface VisitApprovalSummary {
    _id: string;
    name: string;
    customer: Entity;
    reviewContext: VisitApprovalReviewContext;
    currentRevision: number;
    changedAt: Date;
    changedBy: Entity;
    membership: VisitApprovalScopedCounts;
    validity: VisitApprovalEffectiveValidityCounts;
    /** Detail reads only: up to VISIT_APPROVAL_PLAN_COVERAGE_LIMIT of the plans the scoped membership spans, by name. */
    servicePlans?: Entity[];
    /** Detail reads only: every distinct plan the scoped membership spans, so a capped list can say how many it left out. */
    servicePlanCount?: number;
}
/** A customer approval can span a thousand plans; the detail summary names this many and counts the rest. */
export const VISIT_APPROVAL_PLAN_COVERAGE_LIMIT = 10;
/** Detail reads retain the caller's authorised viewing scope; the storage header is not a public detail DTO. */
export interface VisitApprovalGetRequest { approvalId: string; scope: VisitApprovalScope; }
export interface VisitApprovalPage { items: VisitApprovalSummary[]; total: number; nextCursor?: string; readToken: string; }
export interface VisitApprovalEntriesRequest extends VisitApprovalPageRequest {
    approvalId: string;
    /** Each membership carries its approved capture: only a board that shows what was approved asks. */
    withCapture?: boolean;
}
export interface VisitApprovalCurrentEntry { membership: VisitApprovalMembership; effectiveValidity: VisitApprovalValidityState; reason?: string; }
export interface VisitApprovalEntriesPage {
    items: VisitApprovalCurrentEntry[];
    counts: VisitApprovalScopedCounts;
    validity: VisitApprovalEffectiveValidityCounts;
    revision: number;
    readToken: string;
    nextCursor?: string;
}
export interface VisitApprovalHistoryRequest extends VisitApprovalPageRequest { approvalId: string; revision: number; }
/**
 * One Visit as approved at a revision: the latest Added or Reapproved entry at or before it. `membership`
 * is the row's current decision with its capture, present only while that decision is still this entry's;
 * a capture a later revision replaced is not kept.
 */
export interface VisitApprovalHistoryEntry extends VisitApprovalRevisionEntry {
    change: 'Added' | 'Reapproved';
    approvalId: string;
    revision: number;
    approvedBy: Entity;
    approvedAt: Date;
    membership?: VisitApprovalMembership & { capture: VisitApprovalCapturedWork };
}
export interface VisitApprovalHistoryPage { items: VisitApprovalHistoryEntry[]; counts: VisitApprovalScopedCounts; revision: number; nextCursor?: string; }
export interface VisitApprovalRevisionChangesPage { items: VisitApprovalRevisionEntry[]; counts: VisitApprovalScopedCounts; revision: number; nextCursor?: string; }
export interface VisitApprovalRevisionsRequest extends VisitApprovalPageRequest { approvalId: string; }
export interface VisitApprovalScopedRevision {
    _id: string;
    approvalId: string;
    revision: number;
    baseRevision: number;
    operationId: string;
    actor: Entity;
    createdAt: Date;
    context: VisitApprovalReviewContext;
    matchedChanges: VisitApprovalChangeCounts;
    fullChanges?: VisitApprovalChangeCounts;
}
export interface VisitApprovalRevisionsPage { items: VisitApprovalScopedRevision[]; total: number; nextCursor?: string; }
export interface VisitApprovalReviewRequest extends VisitApprovalPageRequest { review: VisitApprovalReviewFilter; }
export interface VisitApprovalReviewRow {
    occurrenceId: string;
    reviewToken: string;
    capture?: VisitApprovalCapturedWork;
    unavailableReason?: string;
    approval?: VisitApprovalAttribution;
    effectiveValidity?: VisitApprovalValidityState;
    /** Section 15: where an approve-and-move date may land; outside the fingerprinted snapshot on purpose. */
    complianceWindow?: VisitComplianceWindow;
}
export interface VisitApprovalReviewPage { items: VisitApprovalReviewRow[]; matchedTotal?: number; hasMore: boolean; nextCursor?: string; readToken: string; }

export type VisitApprovalReviewStatus = 'Awaiting' | 'Approved' | 'Changed' | 'Unavailable';

/**
 * What a board column names, as against what the reader returns.
 *
 * The DTOs above are evidence shapes: the reviewed work sits under `capture.snapshot`, and the two
 * membership counts under `membership`. A board addresses one flat name per column, so the entity
 * strategy flattens each row to these before the view and the registered metadata ever see it — which
 * is also why the metadata declares `startDate` and `site` rather than their snapshot paths.
 */
export interface VisitApprovalSummaryRow extends VisitApprovalSummary {
    matchedTotal?: number;
    fullTotal?: number;
}

export interface VisitApprovalReviewBoardRow extends Entity, VisitApprovalReviewRow {
    _id: string;
    name: string;
    startDate?: Date;
    customer?: Entity;
    site?: Entity;
    servicePlan?: Entity;
    serviceLine?: Entity;
    serviceType?: Entity;
    serviceScope?: Entity;
    approvalStatus: VisitApprovalReviewStatus;
    approvalUnavailableReason?: string;
    taskCount: number;
}

/**
 * One approved visit of an approval, flattened for the board.
 *
 * The values are read off the capture the approval SAVED on the row, never re-derived from the plan
 * today: an approval that re-resolved live would paint this week's work under a period signed months
 * ago, and the drift would be invisible on screen.
 */
export interface VisitApprovalVisitRow {
    _id: string;
    occurrenceId: string;
    approvalId: string;
    revision: number;
    name: string;
    startDate: Date;
    timezone: string;
    site?: Entity;
    servicePlan: Entity;
    serviceLine: Entity;
    approvalState?: VisitApprovalValidityState;
    approvalStateReason?: string;
    taskCount: number;
    itemCount: number;
    /** Who confirmed this Visit's decision, and when. */
    approvedBy: Entity;
    approvedAt: Date;
    /**
     * The current decision with its saved capture, so the row menu and snapshot dialog need no second read.
     * Absent on a revision read once a later revision replaced the decision: its capture is not kept.
     */
    membership?: VisitApprovalMembership & { capture: VisitApprovalCapturedWork };
}

/** One revision of an approval, flattened for the board. */
export interface VisitApprovalRevisionRow {
    _id: string;
    approvalId: string;
    revision: number;
    name: string;
    createdAt: Date;
    actor: Entity;
    added: number;
    reapproved: number;
    withdrawn: number;
    record: VisitApprovalScopedRevision;
}
